export const CUSTOM_FORM_FIELD_TYPES = [
  "short_text",
  "long_text",
  "single_select",
  "multi_select",
  "date",
  "number",
] as const;

export type CustomFormFieldType = (typeof CUSTOM_FORM_FIELD_TYPES)[number];

export interface CustomFormFieldChoice {
  value: string;
  label: string;
}

export interface CustomFormFieldOptions {
  choices?: CustomFormFieldChoice[];
}

export interface CustomFormField {
  id: number;
  uuid: string;
  fieldType: CustomFormFieldType;
  label: string;
  helpText: string | null;
  required: boolean;
  position: number;
  options: CustomFormFieldOptions;
  createdAt: string;
  updatedAt: string;
}

export interface CustomForm {
  id: number;
  uuid: string;
  name: string;
  description: string | null;
  descriptionPlain?: string | null;
  responseScreenHeader: string | null;
  responseScreenText: string | null;
  showSubmitterInfo: boolean;
  companyId: number;
  archivedAt: string | null;
  fieldsCount?: number;
  responsesCount?: number;
  customFormFields?: CustomFormField[];
  archivedReferencedFields?: CustomFormField[];
  createdAt: string;
  updatedAt: string;
}

export interface CustomFormAnswerEntry {
  fieldUUID: string;
  value: string | string[] | number | null;
}

export type CustomFormAnswerMap = Record<string, CustomFormAnswerEntry["value"]>;

// Array shape (not `{ uuid => value }`) — CaseConverter mangles hyphens in hash keys.
export function findAnswer(
  // Accepts the current array shape or a stale hash-shape cache entry from before the blueprint change.
  answers: CustomFormAnswerEntry[] | Record<string, CustomFormAnswerEntry["value"]> | undefined,
  fieldUUID: string,
): CustomFormAnswerEntry["value"] {
  if (!answers) return null;
  if (Array.isArray(answers)) {
    return answers.find((a) => a.fieldUUID === fieldUUID)?.value ?? null;
  }
  return answers[fieldUUID] ?? null;
}

export interface CustomFormResponseFieldSnapshot {
  uuid: string;
  fieldType: CustomFormFieldType;
  label: string;
  helpText: string | null;
  required: boolean;
  position: number;
  options: CustomFormFieldOptions;
}

export interface CustomFormResponse {
  id: number;
  uuid: string;
  customFormId: number;
  taskId: number;
  newHireJourneyId: number;
  companyId: number;
  submittedAt: string;
  submittedBy: {
    id: number;
    fullName: string;
    workEmail: string | null;
  };
  answers: CustomFormAnswerEntry[];
  fieldsSnapshot: CustomFormResponseFieldSnapshot[];
  customForm?: CustomForm;
}

// Task-scoped endpoint returns submission status only; answers require the per-response show.
export type CustomFormResponseSummary = Omit<CustomFormResponse, "answers" | "fieldsSnapshot" | "customForm">;
